import { get } from 'svelte/store';
//Services
import { postReq, localReq } from 'src/services/http';
import { gen2Request } from 'src/services/gen_2_http_reqs';
//Helpers
import { get_local_ip, is_gen_2, isRgbCct, real_id } from './interface_helpers';
import { isRgbwwPmRgbcctProfile } from './light_priority_layout';
//Stores
import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
import { cloud_disabled_devices } from 'src/stores/device-runtime/firmware-ui';
//Types
import type { Device } from 'src/types/Device';
import type { SettingRequest } from 'src/types/requests/settings/setting';
import type { LightMode } from 'src/types/LightType';

/**
 * Switches a single light device between Color and White mode using the same
 * per-generation logic that `ControlMode.svelte` uses for the single-device
 * panel. Used by both the single-device toggle and the bulb-group toggle so
 * they stay in sync.
 *
 * - Gen1 (e.g. `SHCB-1`, `SHBLB-1`): POST to `/settings/{channel}` locally,
 *   falling back to the cloud `change_mode` endpoint.
 * - Gen2 RGB+CCT (`isRgbCct`, e.g. `S3BL-C010007AEU`): `RgbCct.Set` with
 *   `{ id, mode: 'rgb' | 'cct' }`.
 * - Pro RGBWW PM already in `rgbcct`: `RGBCCT.Set` with `{ id, mode, on }`.
 * - Other Gen2 profile devices: `Shelly.SetProfile` with `{ id, name: 'rgb' | 'cct' }`.
 *
 * Caller is responsible for excluding RGBW2 (firmware swap) and any device
 * type returned by `lightDevicesWithoutModeSwitch` BEFORE invoking this,
 * except Pro RGBWW `rgbcct` soft-switch which this helper handles itself.
 */

export type LightModeChangeMethod = 'gen1' | 'rgbCctSet' | 'rgbcctSet' | 'setProfile';

export const resolveLightModeChangeMethod = (
	device: Device,
	settings?: Record<string, any>,
): LightModeChangeMethod => {
	if (!is_gen_2(device)) return 'gen1';
	if (isRgbCct(device.type)) return 'rgbCctSet';
	if (isRgbwwPmRgbcctProfile(device, undefined, settings)) return 'rgbcctSet';
	return 'setProfile';
};
export const changeLightMode = async (
	device: Device,
	settings: any,
	newMode: LightMode,
): Promise<SettingRequest> => {
	const isGen2 = is_gen_2(device);
	const local_ip = get_local_ip(real_id(device.id));

	let res: SettingRequest;

	if (!isGen2) {
		if (local_ip) {
			res = await localReq(
				device,
				`${local_ip}/settings/${device.channel}`,
				'post',
				{ mode: newMode },
				6000,
				true,
			);

			if (!res?.isok && typeof get(cloud_disabled_devices)[device.id] === 'undefined') {
				res = await postReq(`/device/light/settings/change_mode`, {
					id: real_id(device.id),
					channel: device.channel,
					mode: newMode,
				});
			}
		} else {
			res = await postReq(
				`device/light/settings/change_mode`,
				{
					id: real_id(device.id),
					channel: device.channel,
					mode: newMode,
				},
				undefined,
				device.shared ? device.server : undefined,
			);
		}
	} else {
		const actualMode = newMode === 'color' ? 'rgb' : 'cct';
		const methodKind = resolveLightModeChangeMethod(device, settings);
		if (methodKind === 'rgbCctSet' || methodKind === 'rgbcctSet') {
			const status = devices_status.getByRealId(device.id) || {};
			const block = status['rgbcct:0'] || {};
			const on = typeof block.output === 'boolean' ? block.output : true;
			res = await gen2Request(device, methodKind === 'rgbCctSet' ? 'RgbCct.Set' : 'RGBCCT.Set', {
				id: device.channel,
				mode: actualMode,
				on,
			});
		} else {
			res = await gen2Request(device, 'Shelly.SetProfile', {
				id: device.channel,
				name: actualMode,
			});
		}
	}

	if (res?.isok) {
		const methodKind = resolveLightModeChangeMethod(device, settings);
		if (methodKind === 'rgbCctSet' || methodKind === 'rgbcctSet') {
			const actualMode = newMode === 'color' ? 'rgb' : 'cct';
			const next = {
				...(settings || {}),
				'rgbcct:0': {
					...(settings?.['rgbcct:0'] || {}),
					mode: actualMode,
				},
			};
			devices_settings.update_devices_settings(device.id, next);
			const status = devices_status.getByRealId(device.id);
			if (status?.['rgbcct:0']) {
				devices_status.update_device_status({
					id: real_id(device.id),
					msg: {
						...status,
						'rgbcct:0': { ...status['rgbcct:0'], mode: actualMode },
					},
				});
			}
		} else if (settings) {
			const next = { ...settings, mode: newMode };
			devices_settings.update_devices_settings(device.id, next);
		}
	}

	return res;
};

/**
 * Resolves the active Color/White toggle state for any supported light device,
 * mirroring the `colorType` derivation in `ControlMode.svelte`. Used by both
 * the single-device toggle and the bulb-group toggle to highlight the correct
 * button when devices report mode as `rgb` / `cct` / `rgbcct`.
 */
export const resolveLightColorType = (
	device: Device,
	settings: any,
	mode: string | undefined,
): LightMode => {
	if (isRgbCct(device.type) || isRgbwwPmRgbcctProfile(device, mode, settings)) {
		const rgbCctMode = settings?.['rgbcct:0']?.mode;
		return rgbCctMode === 'cct' || (!rgbCctMode && (mode === 'cct' || mode === 'white'))
			? 'white'
			: 'color';
	}
	if (mode === 'rgb' || mode === 'rgbw' || mode === 'color') return 'color';
	if (mode === 'cct' || mode === 'white') return 'white';
	return device.category === 'light' ? (mode as LightMode) || 'color' : 'white';
};
